// booking-store.ts — Lightweight React context-based booking state.
// Threads selectedFare, passengers, selectedSeat, paymentMethod across pages.
// TODO(booking-API-PR): wire state mutations to real BE endpoints.

import * as React from 'react';
import type { FareTier, Passenger, PaymentMethod } from './booking-mock';
import { FARE_TIERS, PASSENGER_DEFAULT } from './booking-mock';

// ─── STATE SHAPE ─────────────────────────────────────────────────────────────
export interface BookingState {
  selectedFare:  FareTier;
  passengers:    Passenger[];
  contactEmail:  string;
  contactPhone:  string;
  ffProgram:     string;
  ffNumber:      string;
  selectedSeat:  string | null;   // e.g. "22A"
  paymentMethod: PaymentMethod;
  insurance:     boolean;
  pnr:           string | null;
}

type BookingAction =
  | { type: 'SET_FARE';          fare: FareTier }
  | { type: 'SET_PASSENGER';     index: number; patch: Partial<Passenger> }
  | { type: 'SET_CONTACT';       email: string; phone: string }
  | { type: 'SET_FF';            program: string; number: string }
  | { type: 'SET_SEAT';          seat: string | null }
  | { type: 'SET_PAYMENT';       method: PaymentMethod }
  | { type: 'TOGGLE_INSURANCE' }
  | { type: 'SET_PNR';           pnr: string };

function reducer(state: BookingState, action: BookingAction): BookingState {
  switch (action.type) {
    case 'SET_FARE':
      return { ...state, selectedFare: action.fare };
    case 'SET_PASSENGER': {
      const passengers = state.passengers.map((p, i) =>
        i === action.index ? { ...p, ...action.patch } : p
      );
      return { ...state, passengers };
    }
    case 'SET_CONTACT':
      return { ...state, contactEmail: action.email, contactPhone: action.phone };
    case 'SET_FF':
      return { ...state, ffProgram: action.program, ffNumber: action.number };
    case 'SET_SEAT':
      return { ...state, selectedSeat: action.seat };
    case 'SET_PAYMENT':
      return { ...state, paymentMethod: action.method };
    case 'TOGGLE_INSURANCE':
      return { ...state, insurance: !state.insurance };
    case 'SET_PNR':
      return { ...state, pnr: action.pnr };
    default:
      return state;
  }
}

const initialState: BookingState = {
  selectedFare:  FARE_TIERS[1]!, // Standard — index 1 is always defined
  passengers:    [{ ...PASSENGER_DEFAULT }],
  contactEmail:  'alex.chen@email.com',
  contactPhone:  '+91 98765 43210',
  ffProgram:     'SQ',
  ffNumber:      '',
  selectedSeat:  null,
  paymentMethod: 'card',
  insurance:     false,
  pnr:           null,
};

// ─── CONTEXT ─────────────────────────────────────────────────────────────────
interface BookingContextValue {
  state:    BookingState;
  dispatch: React.Dispatch<BookingAction>;
}

const BookingContext = React.createContext<BookingContextValue | null>(null);

export function BookingProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const value = React.useMemo(() => ({ state, dispatch }), [state]);
  return React.createElement(BookingContext.Provider, { value }, children);
}

export function useBooking(): BookingContextValue {
  const ctx = React.useContext(BookingContext);
  if (!ctx) throw new Error('useBooking must be used inside BookingProvider');
  return ctx;
}
